import { useEffect } from "react";

type Handler = () => void;

type ShortcutSpec = {
  /** "key" or "g s" (chord with leading prefix). Single chord prefix only. */
  combo: string;
  handler: Handler;
  /** Override the default ignore-when-typing rule (default true). */
  allowInInputs?: boolean;
};

const PREFIX_TIMEOUT_MS = 800;

function isTextual(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName.toUpperCase();
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable
  );
}

/**
 * Single-stroke or two-key-chord keyboard shortcuts.
 *
 *   - `combo: "n"` fires on plain `n`.
 *   - `combo: "g s"` fires on `g` then `s` within 800ms (Gmail/Linear style).
 *   - `combo: "?"` accepts the rendered character ignoring shift.
 *
 * Shortcuts are skipped while the user is typing into an input / textarea /
 * contenteditable unless `allowInInputs: true`. Modifier-key combinations
 * (Cmd/Ctrl/Alt held) are always passed through — we only handle plain keys.
 */
export function useKeyboardShortcuts(specs: ShortcutSpec[]): void {
  useEffect(() => {
    let prefix: string | null = null;
    let prefixTimer: ReturnType<typeof setTimeout> | null = null;

    const clearPrefix = () => {
      prefix = null;
      if (prefixTimer) {
        clearTimeout(prefixTimer);
        prefixTimer = null;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

      // Find a chord match first; chords win over single keys for the same letter.
      if (prefix) {
        const wanted = `${prefix} ${key}`;
        const chord = specs.find((s) => s.combo === wanted);
        clearPrefix();
        if (chord && (chord.allowInInputs || !isTextual(e.target))) {
          e.preventDefault();
          chord.handler();
          return;
        }
        // fall through to single-key check
      }

      const single = specs.find((s) => s.combo === key);
      if (single && (single.allowInInputs || !isTextual(e.target))) {
        e.preventDefault();
        single.handler();
        return;
      }

      // Could this key be the start of a chord? (any spec like "<key> ?")
      const startsChord = specs.some((s) => s.combo.startsWith(`${key} `));
      if (startsChord && !isTextual(e.target)) {
        prefix = key;
        prefixTimer = setTimeout(clearPrefix, PREFIX_TIMEOUT_MS);
      }
    };

    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearPrefix();
    };
  }, [specs]);
}
